import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import {
  setSteps, setConfig, setLoading, isCompleted, setCompleted, getConfig
} from "../../../../redux/walkthroughPreviewSlice";
import SatisfactionScore from '../../../organisms/satisfaction-score/SatisfactionScore'
import { getCompanyDetails } from '../../../../redux/hubSlice';
import StepViewer from '../../paths/StepViewer';
import { HUB_ENDPOINTS } from "../../utils/endpoints";
import { http_get } from "../../utils/hub_client";
import PreviewHeader from '../../paths/PreviewHeader';
import { setMaterial } from '../../../../redux/materialSlice';
import { getValue } from '../../utils/tools';

const POLL_INTERVAL_MS = 3000;
const PATH_PUBLISHED_EVENT_KEY = "un:path:published";

const PreviewPath = ({ id, default_host, audience }) => {
  const dispatch = useDispatch();
  const completed = useSelector((state) => isCompleted(state));
  const company = useSelector((state) => getCompanyDetails(state));
  const pageConfig = useSelector((state) => getConfig(state));
  const [openModal, setOpenModal] = useState(true);
  const lastSignatureRef = useRef(null);
  const inFlightRef = useRef(false);
  const pendingForceRefreshRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const defaultColors = {
    background: '#F6F8F9',
    config: pageConfig
  };
  const baBGColor = getValue(defaultColors, 'ba', 'baHeaderColor');
  const previewSurfaceStyle = {
    background: baBGColor || '#F6F8F9',
    backgroundColor: baBGColor || '#F6F8F9',
    '--path-bottom-bg': baBGColor || '#F6F8F9',
    '--path-bottom-bg-end': baBGColor || '#F6F8F9'
  };

  const submitScore = (score) => {
    setOpenModal(false);
    window.location.href = `/${company.slug}`;
  }

  const handleCloseModal = () => {
    dispatch(setCompleted(false));
  }

  const buildSignature = (config, steps) => {
    return JSON.stringify({
      config: config || [],
      data: steps || []
    });
  };

  const loadMaterial = useCallback(({ silent = false, force = false } = {}) => {
    if (inFlightRef.current) {
      if (force) {
        pendingForceRefreshRef.current = true;
      }
      return Promise.resolve();
    }

    inFlightRef.current = true;
    if (!silent) dispatch(setLoading(true));

    const url = HUB_ENDPOINTS.GET_MATERIAL(id);
    return http_get(url, { _rt: Date.now() })
      .then((response) => {
        const config = Array.isArray(response.data.config) ? response.data.config : [];
        const steps = Array.isArray(response.data.data) ? response.data.data : [];
        const details = { config, ...response.data.details };
        const nextSignature = buildSignature(config, steps);

        if (lastSignatureRef.current === nextSignature) return;

        lastSignatureRef.current = nextSignature;
        dispatch(setSteps(steps));
        dispatch(setConfig(config));
        dispatch(setMaterial(details));
      })
      .finally(() => {
        inFlightRef.current = false;
        if (!silent) dispatch(setLoading(false));

        if (pendingForceRefreshRef.current) {
          pendingForceRefreshRef.current = false;
          loadMaterial({ silent: true, force: false });
        }
      });
  }, [dispatch, id]);

  const getCurrentCompanySlug = () => window.location.pathname.split('/')[1] || "";

  useEffect(() => {
    loadMaterial();

    pollIntervalRef.current = setInterval(() => {
      loadMaterial({ silent: true });
    }, POLL_INTERVAL_MS);

    const onStorageSignal = (event) => {
      if (event.key !== PATH_PUBLISHED_EVENT_KEY || !event.newValue) return;

      try {
        const payload = JSON.parse(event.newValue);
        if (!payload?.slug || payload.slug !== id) return;

        const currentCompanySlug = getCurrentCompanySlug();
        if (payload.companySlug && payload.companySlug !== currentCompanySlug) return;

        loadMaterial({ silent: true, force: true });
      } catch (_error) {
        // Ignore malformed cross-tab storage payloads.
      }
    };

    const onVisibleRefresh = () => {
      if (document.visibilityState === 'visible') {
        loadMaterial({ silent: true, force: true });
      }
    };

    const onFocusRefresh = () => {
      loadMaterial({ silent: true, force: true });
    };

    window.addEventListener('storage', onStorageSignal);
    document.addEventListener('visibilitychange', onVisibleRefresh);
    window.addEventListener('focus', onFocusRefresh);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      window.removeEventListener('storage', onStorageSignal);
      document.removeEventListener('visibilitychange', onVisibleRefresh);
      window.removeEventListener('focus', onFocusRefresh);
    };
  }, [loadMaterial]);

  useEffect(() => {
    if(completed && openModal){
      $('#satisfactoryScore').modal({backdrop: 'static', keyboard: false});
    }
  }, [completed]);

  return (
    <>
      <SatisfactionScore onSubmitScore={submitScore} onCloseModal={handleCloseModal} />
      <div className="un-path-public-page" style={previewSurfaceStyle}>
        <PreviewHeader default_host={default_host} audience={audience} />
        <StepViewer />
      </div>
    </>
  )
}

export default PreviewPath
